import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  ArrowLeft, Plus, Search, Edit, Trash2, AlertTriangle, 
  FileText, User, Phone, Mail, Home 
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const CustomerList = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const url = searchQuery 
        ? `${API_URL}/api/customers?search=${encodeURIComponent(searchQuery)}`
        : `${API_URL}/api/customers`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchCustomers]);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/customers/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setCustomers(prev => prev.filter(c => c.customer_id !== deleteId));
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
    } finally {
      setDeleteId(null);
    }
  };

  // Count expired documents for a customer
  const getExpiredDocCount = (customer) => {
    const docs = customer.documents || {};
    return Object.values(docs).filter(doc => doc && doc.is_expired).length;
  };

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="customer-list-page">
      {/* Header */}
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-[#f5f3ed]">Customers</h1>
          </div>
          <Button
            onClick={() => navigate('/admin/customers/new')}
            className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
            data-testid="add-customer-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Customer
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#f5f3ed]/50" />
          <Input
            type="text"
            placeholder="Search customers by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#2d5f3f]/30 border-[#2d5f3f] text-[#f5f3ed] placeholder:text-[#f5f3ed]/50"
            data-testid="customer-search-input"
          />
        </div>

        {/* Customer List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c9a55a]"></div>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-[#f5f3ed]/30 mx-auto mb-4" />
            <p className="text-[#f5f3ed]/50">
              {searchQuery ? 'No customers found matching your search' : 'No customers yet'}
            </p>
            <Button
              onClick={() => navigate('/admin/customers/new')}
              className="mt-4 bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Customer
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {customers.map(customer => {
              const expiredDocs = getExpiredDocCount(customer);
              
              return (
                <div
                  key={customer.customer_id}
                  className="bg-[#2d5f3f]/30 rounded-xl p-5 border border-[#2d5f3f] hover:border-[#c9a55a]/50 transition-colors"
                  data-testid={`customer-card-${customer.customer_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        {customer.display_id && (
                          <span className="bg-[#c9a55a]/20 text-[#c9a55a] px-2 py-1 rounded text-sm font-mono">
                            {customer.display_id}
                          </span>
                        )}
                        <h3 className="text-lg font-semibold text-[#f5f3ed]">
                          {customer.first_name} {customer.surname}
                        </h3>
                        {expiredDocs > 0 && (
                          <div className="flex items-center gap-1 bg-red-500/20 text-red-400 px-2 py-1 rounded-full text-xs">
                            <AlertTriangle className="w-3 h-3" />
                            {expiredDocs} expired doc{expiredDocs > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-2 space-y-1">
                        {customer.email && (
                          <div className="flex items-center gap-2 text-[#f5f3ed]/70 text-sm">
                            <Mail className="w-4 h-4" />
                            <span>{customer.email}</span>
                          </div>
                        )}
                        {customer.telephone && (
                          <div className="flex items-center gap-2 text-[#f5f3ed]/70 text-sm">
                            <Phone className="w-4 h-4" />
                            <span>{customer.telephone}</span>
                          </div>
                        )}
                        {customer.postcode && (
                          <div className="flex items-center gap-2 text-[#f5f3ed]/70 text-sm">
                            <FileText className="w-4 h-4" />
                            <span>{customer.postcode}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/admin/customers/${customer.customer_id}`)}
                        className="text-[#c9a55a] hover:bg-[#c9a55a]/10"
                        data-testid={`edit-customer-${customer.customer_id}`}
                      >
                        <Edit className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(customer.customer_id)}
                        className="text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-customer-${customer.customer_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1a1f1a] border-[#2d5f3f]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f5f3ed]">Delete Customer?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#f5f3ed]/70">
              This will permanently delete this customer and all their associated bookings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2d5f3f] text-[#f5f3ed] hover:bg-[#2d5f3f]/50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerList;
